import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';

import firebase from '../../Firebase/Firebase';
import { useI18n } from '../../i18n/I18nProvider';

const ProfileLanguageSettings = ({ preferredLanguage, userId }) => {
  const { language, setLanguage, t, pick } = useI18n();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const hydratedPreference = useRef(false);

  // Apply the Firestore preference once when the profile is loaded. Do not
  // keep forcing it on every local language change, otherwise clicking EN is
  // immediately reverted to the old Redux value before Firestore refreshes.
  useEffect(() => {
    if (hydratedPreference.current) return;
    if (preferredLanguage === 'fr' || preferredLanguage === 'en') {
      hydratedPreference.current = true;
      setLanguage(preferredLanguage);
    }
  }, [preferredLanguage, setLanguage]);

  const choose = async (nextLanguage) => {
    if (saving || nextLanguage === language) return;
    setLanguage(nextLanguage);
    setSaved(false);
    if (!userId) return;
    setSaving(true);
    try {
      await firebase.firestore().collection('users').doc(userId).set({
        preferred_language: nextLanguage,
        updated_at: firebase.firestore.FieldValue.serverTimestamp(),
      }, { merge: true });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2200);
    } catch (error) {
      console.warn('Language preference could not be synced.', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card aria-labelledby="profile-language-title">
      <Header>
        <Icon><LanguageOutlinedIcon /></Icon>
        <div>
          <h2 id="profile-language-title">{t('app.language')}</h2>
          <p>{pick(
            'Choisissez la langue utilisée dans votre espace. Vous pourrez la modifier ici à tout moment.',
            'Choose the language used in your account. You can change it here at any time.',
          )}</p>
        </div>
      </Header>
      <Choices role="group" aria-label={t('app.language')}>
        <Choice type="button" $active={language === 'fr'} onClick={() => choose('fr')} aria-pressed={language === 'fr'}>
          <strong>Français</strong><span>FR</span>
        </Choice>
        <Choice type="button" $active={language === 'en'} onClick={() => choose('en')} aria-pressed={language === 'en'}>
          <strong>English</strong><span>EN</span>
        </Choice>
      </Choices>
      <Status aria-live="polite">
        {saving ? pick('Enregistrement…', 'Saving…') : saved ? pick('Langue enregistrée.', 'Language saved.') : ''}
      </Status>
    </Card>
  );
};

const Card = styled.section`
  display:grid; gap:1rem; padding:1.1rem; border:1px solid #e4ddd2; border-radius:1rem; background:#fff;
`;
const Header = styled.div`
  display:flex; gap:.8rem; align-items:flex-start;
  h2{margin:0;font-size:1rem;} p{margin:.25rem 0 0;color:#6f6a63;font-size:.8rem;line-height:1.5;}
`;
const Icon = styled.div`
  display:grid;place-items:center;flex:0 0 2.5rem;height:2.5rem;border-radius:.8rem;background:#f1e8d9;color:#8b6b38;
`;
const Choices = styled.div`
  display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.65rem;
  @media(max-width:430px){grid-template-columns:1fr;}
`;
const Choice = styled.button`
  display:flex;align-items:center;justify-content:space-between;gap:1rem;min-height:3.2rem;padding:.8rem 1rem;
  border:1px solid ${({ $active }) => ($active ? '#111' : '#ddd5ca')};border-radius:.9rem;
  background:${({ $active }) => ($active ? '#111' : '#faf9f7')};color:${({ $active }) => ($active ? '#fff' : '#111')};
  font:inherit;cursor:pointer;text-align:left;
  strong{font-size:.88rem;} span{font-size:.7rem;font-weight:900;opacity:.7;}
`;
const Status = styled.div`min-height:1rem;color:#2f6d49;font-size:.72rem;font-weight:800;`;

const mapStateToProps = ({ firebase: firebaseState }) => ({
  userId: firebaseState.auth?.uid || '',
  preferredLanguage: firebaseState.profile?.preferred_language || '',
});

export default connect(mapStateToProps)(ProfileLanguageSettings);
